import React, { useEffect, useState } from 'react';
import { ErrorToast, SuccessToast } from '../../../utils/toasters';
import { PackagesService } from '../PackagesService';
import { Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import { IPackages } from '../../../model/package/IPackage';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { calculateFee } from '../../../utils/helpers/FeeCaluculation';
import { calculateProfilePrice } from '../../../utils/helpers/DiscoveryProfilePriceCalculation';
import { getCorrectEnums } from '../../../utils/Functions';
import { useSelector } from 'react-redux';

export default function OverrideForm() {
    const { id }: any = useParams();
    const navigate = useNavigate();
    const {
        state: { package: companyPackage, packageOverride },
    }: any = useLocation();
    const { PaymentTermsEnum } = useSelector((state: any) => state.enums);

    const [packageData, setPackageData] = useState<Partial<IPackages>>({});
    const [show, setShow] = useState<boolean>(false);

    useEffect(() => {
        if (!!companyPackage && !!packageOverride) {
            PackagesService.getOverride(packageOverride?.id)
                .then(response => {
                    setPackageData(response);
                })
                .catch(err => ErrorToast(err));
        }
    }, []);

    useEffect(() => {
        if (!!companyPackage && !packageOverride) {
            setPackageData({
                fee: companyPackage.fee,
                monthlyPrice: companyPackage.monthlyPrice,
                isTransparentPricing: companyPackage.isTransparentPricing,
                annualPrice: companyPackage.annualPrice,
                nbEmployees: companyPackage.nbEmployees,
                teamCollaboration: companyPackage.teamCollaboration,
                allowBarterDeals: companyPackage.allowBarterDeals,
                canDoDiscovery: companyPackage?.canDoDiscovery,
                discoveryTokens: companyPackage.discoveryTokens,
                discoveryProfilePrice: companyPackage.discoveryProfilePrice,
                discoverySearchTokens: companyPackage.discoverySearchTokens,
                discoverySearchPrice: companyPackage.discoverySearchPrice,
                canViewDiscoveryBundle: companyPackage?.canViewDiscoveryBundle,
                canEditPaymentTerms: companyPackage?.canEditPaymentTerms,
                paymentTerms: companyPackage?.paymentTerms,
                paymentTermsCustom: companyPackage?.paymentTermsCustom,
                globalDiscovery: companyPackage?.globalDiscovery,
            });
        }
    }, []);

    function handleChange(event: any) {
        const { value, name, type } = event.target;

        let parsedValue = type === 'number' ? parseFloat(value) : value;

        if (name === 'fee') {
            parsedValue = +value > 1 || +value < 0 ? 1 : +value;
        }

        setPackageData({
            ...packageData,
            [name]: parsedValue,
        });
    }

    const updateOverride = () => {
        PackagesService.putOverride(packageOverride?.id, packageData)
            .then(response => {
                setPackageData(response);
                navigate('/company');
                SuccessToast('Successfully updated package');
            })
            .catch(err => ErrorToast(err));
    };

    const createOverride = () => {
        PackagesService.postOverride(id, packageData)
            .then(response => {
                setPackageData(response);
                navigate('/company');
                SuccessToast('Successfully added package');
            })
            .catch(err => ErrorToast(err));
    };

    const deleteOverride = () => {
        PackagesService.deleteOverride(packageOverride?.id)
            .then(() => {
                navigate('/company');
                SuccessToast('Successfully deleted package');
            })
            .catch(err => ErrorToast(err));
    };

    return (
        <div>
            <Card>
                <Form as={Card.Body}>
                    <h1 className="text-black mb-5 text-center">
                        {!!companyPackage && !!packageOverride ? 'Update override' : 'Create override'}
                    </h1>

                    <Row>
                        <Form.Group className="col-6">
                            <Form.Label className="required">Monthly Price (€)</Form.Label>
                            <Form.Control
                                type="number"
                                name="monthlyPrice"
                                onChange={handleChange}
                                defaultValue={packageData.monthlyPrice}
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Label className="required">Annual Price (€)</Form.Label>
                            <Form.Control
                                type="number"
                                name="annualPrice"
                                onChange={handleChange}
                                defaultValue={packageData.annualPrice}
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Label className="required">Employees</Form.Label>
                            <Form.Control
                                type="number"
                                name="nbEmployees"
                                onChange={handleChange}
                                defaultValue={packageData.nbEmployees}
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Label className="required">Fee Currently: {calculateFee(packageData.fee)}</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                min={0}
                                max={1}
                                name="fee"
                                onChange={handleChange}
                                value={packageData.fee}
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Label className="required">Payment terms duration</Form.Label>
                            <Form.Select value={packageData?.paymentTerms} name="paymentTerms" onChange={handleChange}>
                                <option />
                                {PaymentTermsEnum?.map((item: string) => (
                                    <option key={item} value={item}>
                                        {getCorrectEnums(item)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Label>Payment terms description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                maxLength={1000}
                                name="paymentTermsCustom"
                                onChange={handleChange}
                                defaultValue={packageData?.paymentTermsCustom}
                                disabled={packageData?.paymentTerms !== 'other'}
                            />
                            <Form.Text muted>{packageData?.paymentTermsCustom?.length || 0}/1000 characters</Form.Text>
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Check
                                label="Allow Barter Deals"
                                name="allowBarterDeals"
                                onChange={(e: any) =>
                                    setPackageData({ ...packageData, allowBarterDeals: e.target.checked })
                                }
                                checked={packageData.allowBarterDeals}
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Check
                                label="Transparent Pricing"
                                name="isTransparentPricing"
                                checked={packageData?.isTransparentPricing}
                                data-toggle="toggle"
                                onChange={(e: any) =>
                                    setPackageData({ ...packageData, isTransparentPricing: e.target.checked })
                                }
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Check
                                label="Team Collaboration"
                                name="teamCollaboration"
                                checked={packageData?.teamCollaboration}
                                data-toggle="toggle"
                                onChange={(e: any) =>
                                    setPackageData({ ...packageData, teamCollaboration: e.target.checked })
                                }
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Check
                                label="Can Do Long-term"
                                name="canDoLongterm"
                                checked={packageData?.canDoLongTerm}
                                data-toggle="toggle"
                                onChange={(e: any) =>
                                    setPackageData({ ...packageData, canDoLongTerm: e.target.checked })
                                }
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Check
                                label="Can Edit Payment Terms"
                                name="canEditPaymentTerms"
                                checked={packageData?.canEditPaymentTerms}
                                data-toggle="toggle"
                                onChange={(e: any) =>
                                    setPackageData({ ...packageData, canEditPaymentTerms: e.target.checked })
                                }
                            />
                        </Form.Group>
                    </Row>

                    <hr />
                    <h2 className="text-black mb-5 text-center">DISCOVERY</h2>
                    <Row>
                        <Form.Group className="col-6">
                            <Form.Label className="required">
                                Discovery Tokens{' '}
                                {(packageData.canDoDiscovery || packageData?.canViewDiscoveryBundle) && (
                                    <span>
                                        (Views left:{' '}
                                        {calculateProfilePrice(
                                            packageData.discoveryTokens,
                                            packageData.discoveryProfilePrice
                                        )}
                                        )
                                    </span>
                                )}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                name="discoveryTokens"
                                onChange={handleChange}
                                defaultValue={packageData.discoveryTokens}
                                disabled={!packageData?.canDoDiscovery && !packageData?.canViewDiscoveryBundle}
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Label className="required">Discovery Profile Price</Form.Label>
                            <Form.Control
                                type="number"
                                name="discoveryProfilePrice"
                                onChange={handleChange}
                                defaultValue={packageData.discoveryProfilePrice}
                                disabled={!packageData?.canDoDiscovery && !packageData?.canViewDiscoveryBundle}
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Label className="required">
                                Discovery Tokens{' '}
                                {packageData.canDoDiscovery && (
                                    <span>
                                        (Searches left:{' '}
                                        {calculateProfilePrice(
                                            packageData.discoverySearchTokens,
                                            packageData.discoverySearchPrice
                                        )}
                                        )
                                    </span>
                                )}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                name="discoverySearchTokens"
                                onChange={handleChange}
                                defaultValue={packageData.discoverySearchTokens}
                                disabled={!packageData?.canDoDiscovery}
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Label className="required">Discovery Search Price</Form.Label>
                            <Form.Control
                                type="number"
                                name="discoverySearchPrice"
                                onChange={handleChange}
                                defaultValue={packageData.discoverySearchPrice}
                                disabled={!packageData?.canDoDiscovery}
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Check
                                label="Can do Discovery"
                                name="canDoDiscovery"
                                checked={packageData?.canDoDiscovery}
                                onChange={(e: any) =>
                                    setPackageData({ ...packageData, canDoDiscovery: e.target.checked })
                                }
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Check
                                label="Discovery Bundle"
                                name="canViewDiscoveryBundle"
                                checked={packageData?.canViewDiscoveryBundle}
                                onChange={(e: any) =>
                                    setPackageData({ ...packageData, canViewDiscoveryBundle: e.target.checked })
                                }
                            />
                        </Form.Group>
                        <Form.Group className="col-6">
                            <Form.Check
                                label="Global Discovery"
                                name="globalDiscovery"
                                checked={packageData?.globalDiscovery}
                                onChange={(e: any) =>
                                    setPackageData({ ...packageData, globalDiscovery: e.target.checked })
                                }
                            />
                        </Form.Group>
                    </Row>
                    <div className="d-flex justify-content-between align-items-center">
                        <Button
                            onClick={!!companyPackage && !!packageOverride ? updateOverride : createOverride}
                            className="col-md-1 col-sm-12 "
                            size="lg"
                        >
                            Save
                        </Button>
                        {!!companyPackage && !!packageOverride && (
                            <Button
                                onClick={() => setShow(true)}
                                className="col-md-1 col-sm-12"
                                size="lg"
                                variant="danger"
                            >
                                Delete
                            </Button>
                        )}
                    </div>
                </Form>
            </Card>

            <Modal
                show={show}
                onHide={() => setShow(false)}
                size="lg"
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title id="contained-modal-title-vcenter">
                        Are you sure you want to delete this override?
                    </Modal.Title>
                </Modal.Header>
                <Modal.Footer>
                    <Button onClick={deleteOverride}>Yes</Button>
                    <Button variant="danger" onClick={() => setShow(false)}>
                        Decline
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
